import {ConfigObjectType, enumContainer, StateId} from '@wallaby-ui/core';
import {debounce} from 'lodash';
import * as React from 'react';
import {useCallback, useMemo} from 'react';
import {connect, ConnectedProps} from 'react-redux';
import {AGENS_REDUCER_STATE_ID} from 'src/js/containers/agens/agens_constants';
import {getServiceUrls} from 'src/js/containers/filter_and_search/filter_and_search_selectors';
import {changeParamsAndQuery} from 'src/js/containers/filter_and_search/filter_and_search_thunks';
import {
  removeFilterAndQuery,
  removeAllFiltersAndQuery,
} from 'src/js/containers/filtering/filtering_actions';
import {
  getLoadedHierarchyChildrenLabels,
  getNotEmptySelectedFilters,
} from 'src/js/containers/filtering/filtering_selectors';
import {RootState} from 'src/js/store';

import {SelectedFilterBox} from './SelectedFilterBox';
import {useOrganizations} from '../../permissions/usePermissions';

interface OwnProps {
  config: ConfigObjectType;
  filterAndSearchStateId: StateId;
  filteringStateId: StateId;
  serviceUrls: Record<string, any>;
}

const mapState = (state: RootState, props: OwnProps) => ({
  agens: state[AGENS_REDUCER_STATE_ID].filterData,
  autocompleteValues: state[props.filteringStateId].autocompleteValues,
  enumMap: state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap,
  schema: state[props.filteringStateId].schema,
  selectedFilters: getNotEmptySelectedFilters(state, props),
  hierarchyChildrenLabels: getLoadedHierarchyChildrenLabels(state, props),
  serviceUrlsBySelector: getServiceUrls(state, props),
});

const mapDispatch = {
  removeFilterAndQueryAC: removeFilterAndQuery,
  removeAllFiltersAndQueryAC: removeAllFiltersAndQuery,
  changeParamsAndQueryAC: changeParamsAndQuery,
};

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & OwnProps;

export const SelectedFiltersContainerComponent: React.FC<Props> = (props) => {
  const {
    filterAndSearchStateId,
    filteringStateId,
    serviceUrlsBySelector,
    removeFilterAndQueryAC,
    removeAllFiltersAndQueryAC,
    agens,
    autocompleteValues,
    enumMap,
    hierarchyChildrenLabels,
    schema,
    selectedFilters,
    changeParamsAndQueryAC,
    config,
  } = props;
  const {data: organizations} = useOrganizations(config);

  const debouncedChangeParamsAndQuery = useMemo(
    () => debounce(changeParamsAndQueryAC, 500).bind(this),
    [changeParamsAndQueryAC]
  );

  const removeFilter = useCallback(
    (filter: any) => {
      removeFilterAndQueryAC(
        serviceUrlsBySelector.searchService,
        filter,
        debouncedChangeParamsAndQuery,
        filteringStateId,
        filterAndSearchStateId
      );
    },
    [
      debouncedChangeParamsAndQuery,
      filterAndSearchStateId,
      filteringStateId,
      removeFilterAndQueryAC,
      serviceUrlsBySelector.searchService,
    ]
  );

  const removeAllFilters = () => {
    removeAllFiltersAndQueryAC(
      serviceUrlsBySelector.searchService,
      debouncedChangeParamsAndQuery,
      filteringStateId,
      filterAndSearchStateId
    );
  };

  return (
    <SelectedFilterBox
      autocompleteValues={autocompleteValues}
      enumMap={enumMap}
      agens={agens}
      hierarchyChildrenLabels={hierarchyChildrenLabels}
      organizations={organizations}
      selectedFilters={selectedFilters}
      schema={schema}
      removeAllFilters={removeAllFilters}
      removeFilter={removeFilter}
    />
  );
};

SelectedFiltersContainerComponent.displayName = 'SelectedFiltersContainer';

export const SelectedFiltersContainer = connector(
  SelectedFiltersContainerComponent
);
